import type { Bar } from '../chart/chartModel';
import { appliedSeries, type AppliedPrice, type MaMethod, type Series } from './math';

export type ParamValue = number | string;
export type Params = Record<string, ParamValue>;

export type ParamDef =
  | { key: string; label: string; type: 'int'; default: number; min: number; max: number }
  | { key: string; label: string; type: 'float'; default: number; min: number; max: number; step: number }
  | { key: string; label: string; type: 'method'; default: MaMethod }
  | { key: string; label: string; type: 'applied'; default: AppliedPrice };

/** MT5's "Apply to: Previous indicator's data" and "First indicator's data". */
export const INDICATOR_SOURCES = ['prev', 'first'] as const;
export type IndicatorSource = (typeof INDICATOR_SOURCES)[number];
export const isIndicatorSource = (value: unknown): value is IndicatorSource =>
  (INDICATOR_SOURCES as readonly unknown[]).includes(value);

export interface OutputDef {
  key: string;
  label: string;
  style: 'line' | 'histogram' | 'dots';
  color: string;
  /**
   * Bar-by-bar colours (MT5's colour buffers, e.g. the Awesome Oscillator's green and red bars): the index into `palette`
   * for bar `i`. The first palette colour is the one the user picks.
   */
  palette?: string[];
  paletteIndex?: (computed: Record<string, Series>, i: number, bars: Bar[]) => number;
  /** Bars the output is drawn ahead of (positive) or behind (negative) the bar it was computed on. */
  shift?: (params: Params) => number;
}

export interface IndicatorDef {
  id: string;
  name: string;
  group: 'Trend' | 'Oscillators' | 'Volatility' | 'Volumes' | 'Bill Williams';
  /** Drawn on the price chart (true) or in its own window below it (false). */
  overlay: boolean;
  params: ParamDef[];
  outputs: OutputDef[];
  /** Horizontal reference lines in an oscillator window, e.g. 30 and 70 for RSI. */
  levels?: number[];
  /** `source` is the other indicator's first line when the "applied" parameter says to use it. */
  compute: (bars: Bar[], params: Params, source?: Series) => Record<string, Series>;
  /** Can be applied to another indicator's data (its "applied" parameter offers "previous/first indicator"). */
  sourceable?: boolean;
  /** The name shown on the chart, with the parameters: "SMA (20)". */
  title: (params: Params) => string;
}

// A palette that reads on the dark and the light themes.
export const C = {
  amber: '#f5a623',
  blue: '#4aa3ff',
  pink: '#ff5fa2',
  green: '#3ecf8e',
  violet: '#a78bfa',
  cyan: '#22d3ee',
  red: '#ef5350',
  grey: '#9aa0b4',
};

export const int = (key: string, label: string, def: number, min = 1, max = 500): ParamDef => ({
  key,
  label,
  type: 'int',
  default: def,
  min,
  max,
});
export const float = (
  key: string,
  label: string,
  def: number,
  min: number,
  max: number,
  step: number,
): ParamDef => ({
  key,
  label,
  type: 'float',
  default: def,
  min,
  max,
  step,
});
export const method = (key: string, label: string, def: MaMethod = 'sma'): ParamDef => ({
  key,
  label,
  type: 'method',
  default: def,
});
export const applied = (key: string, label: string, def: AppliedPrice = 'close'): ParamDef => ({
  key,
  label,
  type: 'applied',
  default: def,
});

export const num = (p: Params, key: string) => Number(p[key]);
/**
 * The series an indicator is computed on: a price of the bars, or, for "previous/first indicator's data", the first line of
 * that indicator (passed in as `source`; it may hold nulls where that indicator has no value yet).
 */
export const price = (bars: Bar[], p: Params, key = 'applied', source?: Series): Series =>
  isIndicatorSource(p[key]) ? (source ?? bars.map(() => null)) : appliedSeries(bars, p[key] as AppliedPrice);
export const upper = (s: string) => s.toUpperCase();

/** Population standard deviation of the last `period` values around `mean` (MT5's definition). */
export function deviation(values: Series, means: Series, period: number): Series {
  return values.map((_, i) => {
    const m = means[i];
    if (m === null || m === undefined || i < period - 1) return null;
    let sum = 0;
    for (let k = i - period + 1; k <= i; k++) {
      const v = values[k];
      if (v === null || v === undefined) return null;
      sum += (v - m) ** 2;
    }
    return Math.sqrt(sum / period);
  });
}
